<template>
  <section class="quote-section general-information">
    <h3>General Information</h3>
    <div class="inputs">
      <TextBox :modelValue="quote.QUOTENUM ? `#${quote.QUOTENUM}` : 'Auto Generated'" label="Quote Number" readonly class="input-item" />
      <TextBox :modelValue="formatDateTime(quote.CREATEDATE)" label="Date Created" readonly class="input-item" />
      <Dropdown v-model="quote.STATUS" :options="statusOptions" label="Status" class="input-item" />
      <TextBox v-model="quote.VALIDTO" label="Valid To" type="date" class="input-item" :required="true" :invalid="showValidationErrors && !quote.VALIDTO" />

      <SearchMenu
        :options="customerMatches"
        label="Contact"
        placeholder="Search Companies or Individuals..."
        v-model="customerSearchText"
        :clearOnFocus="true"
        :required="true"
        :invalid="showValidationErrors && !quote.COMPANYID && !quote.CONTACTID"
        :showExtLink="!!quote.COMPANYID || !!quote.CONTACTID"
        class="input-item"
        @updateContact="assignCustomer"
        @forceOptionsUpdate="customerForceUpdate"
        @blurred="syncCustomerSearchText"
        @navigateToLink="navigateToCustomer" />
      <div class="contact-dropdown input-item" @click="handleContactClick">
        <Dropdown
          v-model="secondaryContactId"
          :options="contactOptions"
          label="Company Contact"
          :canBeEmpty="true"
          :disabled="!quote.COMPANYID"
          :tabindex="isIndividualCustomer ? '-1' : '0'" />
      </div>
      <TextBox v-model="quote.JOBNAME" label="Job Name" class="input-item" :required="true" :invalid="showValidationErrors && !quote.JOBNAME" />
      <Dropdown v-model="quote.ASSIGNEDID" :options="salespersonOptions" label="Salesperson" class="input-item" :invalid="showValidationErrors && !quote.ASSIGNEDID" />

      <TextBox :modelValue="cmsId" label="CMS ID" readonly class="input-item" />
      <TextBox
        :modelValue="contactEmail"
        label="Email"
        readonly
        class="input-item"
        :showExtLink="contactEmail?.includes('@')"
        @navigateToLink="navigateToEmail" />
      <TextBox :modelValue="contactCellPhone" label="Cell Phone" format="phone" readonly class="input-item" />
      <TextBox :modelValue="primaryPhone" label="Phone" :format="isIndividualCustomer ? '' : 'phone'" readonly class="input-item" />
    </div>
  </section>
</template>

<script>
import TextBox from '@/components/inputs/TextBox.vue';
import Dropdown from '@/components/inputs/Dropdown.vue';
import SearchMenu from '@/components/inputs/SearchMenu.vue';
import useSearchWorker from '@/composables/useSearchWorker';
import useCompaniesQuery from '@/composables/useCompaniesQuery';
import useContactsQuery from '@/composables/useContactsQuery';
import useAlerts from '@/composables/useAlerts';
import { getPhoneNumber } from '@/composables/usePhoneTypes';

export default {
  name: 'QuoteGeneralInformation',
  components: {
    TextBox,
    Dropdown,
    SearchMenu,
  },
  emits: ['customerChanged'],
  props: {
    quote: {
      type: Object,
      required: true,
    },
    selectedCompany: {
      type: Object,
      default: null,
    },
    selectedIndividual: {
      type: Object,
      default: null,
    },
    contactOptions: {
      type: Array,
      default: () => [],
    },
    salespersonOptions: {
      type: Array,
      default: () => [],
    },
    showValidationErrors: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    const { searchResults: customerSearchResults, searchText: customerWorkerSearchText, forceUpdate: customerForceUpdate, type: customerSearchType, excludeInactive: customerExcludeInactive } = useSearchWorker();
    customerSearchType.value = 'all';
    customerExcludeInactive.value = true;

    return {
      addAlert,
      customerSearchResults,
      customerWorkerSearchText,
      customerForceUpdate,
      companies: useCompaniesQuery.companies,
      contacts: useContactsQuery.contacts,
    };
  },
  data() {
    return {
      customerSearchText: '',
      statusOptions: [
        { text: 'Open', value: 'open' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Converted', value: 'converted' },
      ],
    };
  },
  computed: {
    customerMatches() {
      const matches = [
        {
          text: 'New Company',
          value: '',
          type: 'new',
          entityType: 'company',
        },
        {
          text: 'New Individual',
          value: '',
          type: 'new',
          entityType: 'contact',
        },
      ];
      matches.push(...this.customerSearchResults.slice(0, 7));
      return matches;
    },
    isIndividualCustomer() {
      return !this.quote?.COMPANYID && !!this.quote?.CONTACTID;
    },
    secondaryContactId: {
      get() {
        return this.quote?.COMPANYID ? this.quote.CONTACTID : null;
      },
      set(value) {
        if (this.quote?.COMPANYID) this.quote.CONTACTID = value;
      },
    },
    companyData() {
      return this.selectedCompany || this.quote?.company || null;
    },
    selectedContact() {
      if (!this.quote?.CONTACTID) return null;
      if (this.isIndividualCustomer) return this.selectedIndividual || this.quote?.contact || null;
      const fromCompany = this.selectedCompany?.contacts?.find((contact) => contact.CONTACTID === this.quote.CONTACTID);
      return fromCompany || this.quote?.contact || null;
    },
    cmsId() {
      return this.companyData?.CMSID ?? '';
    },
    contactEmail() {
      return this.selectedContact?.email?.ADDRESS || '';
    },
    contactCellPhone() {
      return getPhoneNumber(this.selectedContact?.phones, 'mobile');
    },
    primaryPhone() {
      if (this.isIndividualCustomer) {
        return getPhoneNumber(this.selectedContact?.phones, 'direct');
      }
      return getPhoneNumber(this.companyData?.phones, 'main') || getPhoneNumber(this.companyData?.phones, 'tollFree');
    },
  },
  watch: {
    customerSearchText(value) {
      this.customerWorkerSearchText = value;
    },
    'quote.COMPANYID': {
      handler() {
        this.syncCustomerSearchText();
      },
      immediate: true,
    },
    'quote.CONTACTID'() {
      this.syncCustomerSearchText();
    },
    'quote.company.NAME'() {
      this.syncCustomerSearchText();
    },
    'quote.contact.FIRSTNAME'() {
      this.syncCustomerSearchText();
    },
    'quote.contact.LASTNAME'() {
      this.syncCustomerSearchText();
    },
    companies: {
      handler() {
        this.syncCustomerSearchText();
      },
      deep: true,
    },
    contacts: {
      handler() {
        this.syncCustomerSearchText();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.syncCustomerSearchText();
      },
      deep: true,
    },
    selectedIndividual: {
      handler() {
        this.syncCustomerSearchText();
      },
      deep: true,
    },
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return '';
      // Only treat pure numeric values as epoch timestamps (ISO strings like "2026-..." must not use parseInt)
      const date = /^\d+$/.test(String(dateString)) ? new Date(parseInt(dateString, 10)) : new Date(dateString);
      if (date.toString() === 'Invalid Date') return '';
      return date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
    },
    getSelectedCustomerName() {
      if (this.quote?.COMPANYID) {
        if (this.quote.company?.NAME) return this.quote.company.NAME;
        if (this.selectedCompany?.NAME) return this.selectedCompany.NAME;
        const company = this.companies.find((item) => item.COMPANYID?.toUpperCase() === this.quote.COMPANYID?.toUpperCase());
        return company?.NAME || '';
      }

      if (!this.quote?.CONTACTID) return '';
      const contact =
        this.selectedIndividual ||
        this.quote.contact ||
        this.contacts.find((item) => item.CONTACTID?.toUpperCase() === this.quote.CONTACTID?.toUpperCase());
      return `${contact?.FIRSTNAME || ''} ${contact?.LASTNAME || ''}`.trim();
    },
    syncCustomerSearchText() {
      this.customerSearchText = this.getSelectedCustomerName();
    },
    assignCustomer(option) {
      if (!option) return;

      if (option.type === 'new') {
        const route = option.entityType === 'contact' ? { name: 'contacts', params: { contactid: 'new' } } : { name: 'companies', params: { companyid: 'new' } };
        this.$router.push(route);
        this.syncCustomerSearchText();
        return;
      }

      if (option.type === 'company') {
        // Prefer original casing from the companies cache (search worker lowercases IDs)
        const matchedCompany = this.companies.find((company) => company.COMPANYID?.toUpperCase() === option.value?.toUpperCase());
        const companyId = matchedCompany?.COMPANYID || option.value;
        const customerChanged = !this.quote.COMPANYID || this.quote.COMPANYID.toUpperCase() !== companyId?.toUpperCase();
        this.quote.COMPANYID = companyId;
        this.quote.company = { COMPANYID: companyId, NAME: option.text };
        this.customerSearchText = option.text;
        if (customerChanged) {
          this.quote.CONTACTID = null;
          this.quote.contact = null;
          this.$emit('customerChanged');
        }
        return;
      }

      if (option.type === 'contact') {
        const matchedContact = this.contacts.find((contact) => contact.CONTACTID?.toUpperCase() === option.value?.toUpperCase());
        const contactId = matchedContact?.CONTACTID || option.value;
        const customerChanged = !!this.quote.COMPANYID || !this.quote.CONTACTID || this.quote.CONTACTID.toUpperCase() !== contactId?.toUpperCase();
        this.quote.COMPANYID = null;
        this.quote.CONTACTID = contactId;
        this.quote.company = null;
        this.quote.contact = {
          CONTACTID: contactId,
          FIRSTNAME: matchedContact?.FIRSTNAME || option.text,
          LASTNAME: matchedContact?.LASTNAME || '',
        };
        this.customerSearchText = option.text;
        if (customerChanged) this.$emit('customerChanged');
      }
    },
    handleContactClick() {
      if (!this.quote.COMPANYID) {
        this.addAlert(this.isIndividualCustomer ? 'Secondary contacts are only available for company quotes' : 'Please select a company first', 'high', 3);
      }
    },
    navigateToCustomer() {
      if (this.quote.COMPANYID) {
        this.$router.push({ name: 'companies', params: { companyid: this.quote.COMPANYID } });
      } else if (this.quote.CONTACTID) {
        this.$router.push({ name: 'contacts', params: { contactid: this.quote.CONTACTID } });
      }
    },
    navigateToEmail() {
      if (!this.contactEmail) return;
      window.open(`mailto:${this.contactEmail}`, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-section {
  h3 {
    margin: 0 0 1.5rem;
    color: $textColor87;
  }

  .inputs {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.5rem 1rem;

    .input-item {
      min-width: 0;
      width: 100%;
    }

    .contact-dropdown {
      display: flex;
      flex-direction: column;
    }

    .contact-dropdown :deep(.custom-select) {
      width: 100%;
    }
  }

  @media (max-width: 75rem) {
    .inputs {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 40rem) {
    .inputs {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
